import { useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CheckboxSelect,
  CheckboxInput,
  DataGrid,
  ExportButtonGroup,
  FormActions,
  FormSection,
  Input,
  KpiCard,
  Modal,
  MultiSelect,
  ProgressBar,
  ProgressRing,
  RadioGroupInput,
  SelectInput,
  Skeleton,
  Spinner,
  Spreadsheet,
  StarRating,
  StatusBadge,
  StatusCard,
  SummaryCard,
  SwitchInput,
  Tabs,
  TextareaInput,
  TextInput,
  ThemePicker,
  ThemeToggle,
  Tooltip,
  WidgetShell,
  BaseAreaChart,
  BaseBarChart,
  BaseDonutChart,
  MiniSparkline,
  type DataGridColumn,
  type Matrix,
  type CellBase,
} from '@kss/ui';

interface Row extends Record<string, unknown> {
  id: number;
  name: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
}

const rows: Row[] = Array.from({ length: 28 }, (_, i) => ({
  id: i + 1,
  name: `User ${i + 1}`,
  email: `u${i + 1}@kss.app`,
  role: ['Admin', 'Editor', 'Viewer'][i % 3],
  status: (['active', 'inactive', 'pending'] as const)[i % 3],
}));

const columns: DataGridColumn<Row>[] = [
  { accessor: 'id', title: '#', width: 60, sortable: true },
  { accessor: 'name', title: 'Name', sortable: true },
  { accessor: 'email', title: 'Email', sortable: true },
  { accessor: 'role', title: 'Role', sortable: true },
  { accessor: 'status', title: 'Status', render: (r) => <StatusBadge status={r.status} /> },
];

const TAG_OPTIONS = [
  { value: 'react', label: 'React' },
  { value: 'vue', label: 'Vue' },
  { value: 'svelte', label: 'Svelte' },
  { value: 'angular', label: 'Angular' },
  { value: 'solid', label: 'Solid' },
  { value: 'qwik', label: 'Qwik' },
  { value: 'astro', label: 'Astro' },
];

const PERM_OPTIONS = [
  { value: 'users.read', label: 'Read users', group: 'Users' },
  { value: 'users.write', label: 'Create / edit users', group: 'Users' },
  { value: 'users.delete', label: 'Delete users', group: 'Users' },
  { value: 'billing.read', label: 'View billing', group: 'Billing' },
  { value: 'billing.write', label: 'Manage billing', group: 'Billing' },
  { value: 'reports.export', label: 'Export reports', group: 'Reports' },
  { value: 'reports.schedule', label: 'Schedule reports', group: 'Reports' },
];

const initialSheet: Matrix<CellBase> = [
  [{ value: 'Product' }, { value: 'Qty' }, { value: 'Price' }, { value: 'Total' }],
  [{ value: 'Coffee' }, { value: '2' }, { value: '150' }, { value: '300' }],
  [{ value: 'Sandwich' }, { value: '1' }, { value: '220' }, { value: '220' }],
  [{ value: 'Juice' }, { value: '3' }, { value: '80' }, { value: '240' }],
];

const area = [
  { month: 'Jan', value: 120 }, { month: 'Feb', value: 180 }, { month: 'Mar', value: 150 },
  { month: 'Apr', value: 220 }, { month: 'May', value: 260 }, { month: 'Jun', value: 310 },
];
const bar = [
  { name: 'Eng', value: 45 }, { name: 'Design', value: 28 }, { name: 'Sales', value: 60 },
  { name: 'Support', value: 32 },
];
const donut = [
  { name: 'Active', value: 620 }, { name: 'Inactive', value: 140 }, { name: 'Pending', value: 90 },
];

export default function Components() {
  const [modalOpen, setModalOpen] = useState(false);
  const [switchOn, setSwitchOn] = useState(true);
  const [check, setCheck] = useState(false);
  const [radio, setRadio] = useState('a');
  const [sel, setSel] = useState('en');
  const [sheet, setSheet] = useState<Matrix<CellBase>>(initialSheet);
  const [tags, setTags] = useState<string[]>(['react', 'vue']);
  const [perms, setPerms] = useState<string[]>(['users.read', 'billing.read']);

  return (
    <div className="grid gap-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Components</h1>
        <p className="text-sm text-content-secondary">
          All <code>@kss/ui</code> primitives, including the new Multi-select and Checkbox-select dropdowns.
        </p>
      </div>

      <Tabs
        tabs={[
          {
            id: 'primitives',
            label: 'Primitives',
            content: (
              <div className="grid gap-4">
                <Card title="Buttons">
                  <div className="flex flex-wrap gap-2">
                    <Button>Primary</Button>
                    <Button variant="secondary">Secondary</Button>
                    <Button variant="outline">Outline</Button>
                    <Button variant="ghost">Ghost</Button>
                    <Button variant="danger">Danger</Button>
                    <Button loading>Loading</Button>
                    <Button disabled>Disabled</Button>
                  </div>
                </Card>

                <Card title="Badges, Avatar, Spinner, Tooltip, Theme">
                  <div className="flex flex-wrap items-center gap-3">
                    <Badge>Default</Badge>
                    <Badge variant="success">Success</Badge>
                    <Badge variant="warning">Warning</Badge>
                    <Badge variant="danger">Danger</Badge>
                    <Badge variant="info">Info</Badge>
                    <Avatar name="Jane Doe" />
                    <Avatar name="KSS" size={40} />
                    <Spinner />
                    <Tooltip label="Tooltip text">
                      <Button variant="outline" size="sm">Hover me</Button>
                    </Tooltip>
                    <ThemeToggle />
                    <ThemePicker />
                  </div>
                </Card>

                <Card title="Alerts">
                  <div className="grid gap-2">
                    <Alert variant="info" title="Info">Informational alert</Alert>
                    <Alert variant="success" title="Success">It worked</Alert>
                    <Alert variant="warning" title="Warning">Be careful</Alert>
                    <Alert variant="danger" title="Danger">Something went wrong</Alert>
                  </div>
                </Card>

                <Card title="Modal & Skeleton">
                  <div className="flex items-center gap-3">
                    <Button onClick={() => setModalOpen(true)}>Open modal</Button>
                    <Skeleton width={200} height={16} />
                    <Skeleton width={48} height={48} rounded="full" />
                  </div>
                  <Modal
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title="Example modal"
                    footer={
                      <>
                        <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                        <Button onClick={() => setModalOpen(false)}>Confirm</Button>
                      </>
                    }
                  >
                    <p className="text-sm">Modal body content.</p>
                  </Modal>
                </Card>
              </div>
            ),
          },

          {
            id: 'dropdowns',
            label: 'Dropdowns',
            content: (
              <div className="grid gap-4 md:grid-cols-2">
                <Card title="MultiSelect — chip dropdown">
                  <MultiSelect
                    label="Favourite frameworks"
                    value={tags}
                    onChange={setTags}
                    options={TAG_OPTIONS}
                    placeholder="Pick frameworks..."
                    maxSelected={5}
                  />
                  <div className="mt-3 text-xs text-content-muted">
                    Selected: {tags.length === 0 ? 'none' : tags.join(', ')}
                  </div>
                </Card>

                <Card title="CheckboxSelect — grouped checkboxes">
                  <CheckboxSelect
                    label="Permissions"
                    value={perms}
                    onChange={setPerms}
                    options={PERM_OPTIONS}
                    placeholder="Choose permissions"
                    width={300}
                  />
                  <div className="mt-3 text-xs text-content-muted">
                    {perms.length} selected
                  </div>
                </Card>

                <Card title="Single select" className="md:col-span-2">
                  <div className="grid gap-3 md:grid-cols-2">
                    <Input label="Text input" placeholder="type here..." />
                    <SelectInput
                      label="Language"
                      name="lang"
                      value={sel}
                      onChange={(e) => setSel(e.target.value)}
                      options={[
                        { value: 'en', label: 'English' },
                        { value: 'hi', label: 'Hindi' },
                        { value: 'fr', label: 'French' },
                      ]}
                    />
                  </div>
                </Card>
              </div>
            ),
          },

          {
            id: 'forms',
            label: 'Forms',
            content: (
              <Card>
                <FormSection title="Profile" description="Tell us about yourself">
                  <TextInput label="First name" name="firstName" placeholder="Jane" />
                  <TextInput label="Last name" name="lastName" placeholder="Doe" />
                  <SelectInput
                    label="Language"
                    name="language"
                    value={sel}
                    onChange={(e) => setSel(e.target.value)}
                    options={[
                      { value: 'en', label: 'English' },
                      { value: 'hi', label: 'Hindi' },
                    ]}
                  />
                  <TextareaInput label="Bio" name="bio" placeholder="A few words..." rows={3} />
                </FormSection>
                <div className="mt-4">
                  <FormSection title="Preferences">
                    <SwitchInput label="Email notifications" name="emailNotif" checked={switchOn} onChange={setSwitchOn} />
                    <CheckboxInput label="I agree to terms" name="terms" checked={check} onChange={(e) => setCheck(e.target.checked)} />
                    <RadioGroupInput
                      label="Plan"
                      name="plan"
                      value={radio}
                      onChange={(e) => setRadio(e.target.value)}
                      options={[
                        { value: 'a', label: 'Starter' },
                        { value: 'b', label: 'Pro' },
                        { value: 'c', label: 'Team' },
                      ]}
                    />
                  </FormSection>
                </div>
                <FormActions onCancel={() => {}} onSave={() => {}} />
              </Card>
            ),
          },

          {
            id: 'data',
            label: 'Data & Widgets',
            content: (
              <div className="grid gap-4">
                <div className="grid gap-4 md:grid-cols-4">
                  <KpiCard title="Users" value="1,284" trend={{ direction: 'up', value: 12.4 }} />
                  <KpiCard title="Revenue" value="₹ 4.2L" trend={{ direction: 'up', value: 5.1 }} />
                  <KpiCard title="Tickets" value="38" trend={{ direction: 'down', value: 2 }} />
                  <KpiCard title="Emails" value="9,120" trend={{ direction: 'neutral', value: 0 }} />
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <StatusCard title="Server" priority="info" description="All systems nominal" />
                  <SummaryCard
                    items={[
                      { label: 'Avg rating', value: '4.7 / 5' },
                      { label: 'Reviews', value: '1,284' },
                    ]}
                  />
                  <Card title="Rating"><StarRating rating={4.2} /></Card>
                </div>
                <WidgetShell title="Monthly trend">
                  <BaseAreaChart data={area} areas={[{ dataKey: 'value', name: 'Revenue' }]} xAxisKey="month" height={220} />
                </WidgetShell>
                <div className="grid gap-4 md:grid-cols-2">
                  <WidgetShell title="Department breakdown">
                    <BaseBarChart data={bar} bars={[{ dataKey: 'value', name: 'Count' }]} xAxisKey="name" height={220} />
                  </WidgetShell>
                  <WidgetShell title="Status distribution">
                    <BaseDonutChart data={donut} height={220} />
                  </WidgetShell>
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <Card title="Sparkline"><MiniSparkline data={area.map((d) => d.value)} /></Card>
                  <Card title="Progress bar"><ProgressBar value={72} showLabel /></Card>
                  <Card title="Progress ring"><ProgressRing value={64} /></Card>
                </div>
                <Card title="Export">
                  <ExportButtonGroup onExcel={() => {}} onPdf={() => {}} onPrint={() => window.print()} />
                </Card>
              </div>
            ),
          },

          {
            id: 'grid',
            label: 'DataGrid',
            content: (
              <DataGrid<Row>
                title="Users — Mantine DataGrid"
                data={rows}
                columns={columns}
                selectable
                defaultPageSize={10}
                pageSizes={[5, 10, 25, 50]}
              />
            ),
          },

          {
            id: 'sheet',
            label: 'Spreadsheet',
            content: (
              <Card title="Editable spreadsheet">
                <Spreadsheet data={sheet} onChange={setSheet} />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
